import type { UserDetails } from '@/features/users/types';

/**
 * Приведение записи пользователя из API к модели кабинета.
 * Форма записи одинакова в `/init` (`me` и `users.items`) и в `/users`.
 *
 * Часть колонок таблицы бэкенд пока не отдаёт — они заполняются заглушками,
 * список полей в MOCKED_USER_FIELDS, в интерфейсе такие колонки помечены.
 */

export interface RawWallet {
  item_id?: string;
  balance?: number;
  transactions?: unknown[];
  created_at?: string;
}

export interface RawRole {
  item_id?: string;
  title?: string;
}

export interface RawUser {
  item_id?: string;
  is_blocked?: boolean;
  name?: string;
  surname?: string;
  phone_number?: string;
  created_at?: string;
  wallet?: RawWallet;
  roles?: RawRole[];
}

/** Чего ещё нет в API: заполняется заглушками и помечается в таблице. */
export const MOCKED_USER_FIELDS = ['queue'] as const;

const MOCK_QUEUES = ['AKVA', 'TechLine', 'NovaGroup', 'QazaqTrade', ''];

/** Устойчивый разброс заглушек: одна и та же запись всегда выглядит одинаково. */
function hashOf(value: string): number {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash;
}

function fullName(raw: RawUser): string {
  return [raw.name, raw.surname]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
}

export function mapUser(raw: RawUser): UserDetails {
  const id = raw.item_id ?? '';
  const hash = hashOf(id);
  const queueName = MOCK_QUEUES[hash % MOCK_QUEUES.length];

  return {
    id,
    name: fullName(raw),
    phone: raw.phone_number ?? '',
    balance: typeof raw.wallet?.balance === 'number' ? raw.wallet.balance : 0,
    roles: (raw.roles ?? []).map((role) => role.title?.trim() ?? '').filter(Boolean),
    registeredAt: raw.created_at ?? '',

    // ↓ заглушки: этих полей в API пока нет
    queue: queueName ? { inQueue: true, carWashName: queueName } : { inQueue: false },
    status: raw.is_blocked ? 'blocked' : 'active',
    // Колонка «Последняя активность» временно убрана из таблицы; поле оставлено
    // для карточки пользователя, пока в API нет настоящего значения.
    lastActivityAt: raw.created_at ?? '',

    operationHistory: [],
    queueHistory: [],
    recentActivity: [],
  };
}
